import { t } from "i18next";
import { Trans } from "react-i18next";
import { Drawer, DrawerContent, DrawerTitle } from "./ui/drawer";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "./ui/form";
import PhoneField from "./form/PhoneInput";

import { cn } from "../lib/utils";
import { useSendOtp } from "../modules/auth/services/sendOtp";
import { useVerifyOtp } from "../modules/auth/services/verifyOtp";
import { LoadingButton } from "./ui/loadingButton";
import { useState } from "react";
import { ArrowRightIcon } from "../assets/ArrowRight";
import { formatPhoneNumberWithCode } from "../utils/formatPhone";
import { CustomOTP } from "./form/CustomOTP";
import { Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "../store/slices/userSlice";
import { userInfo } from "../store/selectors/user";
import { useRegisterClient } from "../modules/auth/services/registerClient";
import InputField from "./form/InputFiled";
import { NAME_MAX_LENGTH, nameValueSchema, sanitizeName } from "../utils/name";
import { toOtpAuthError } from "../modules/auth/libs/otpAuthError";
import {
  DEFAULT_RESEND_AFTER,
  LIMIT_RETRY_AFTER,
  formatCountdown,
  useCountdown,
} from "../modules/auth/libs/useCountdown";
import { openExternalLink } from "../utils/telegramLink";

const phoneSchema = z.object({
  phone: z.string(),
});
type PhoneFormSchema = z.infer<typeof phoneSchema>;

const otpSchema = z.object({
  otp: z.string().min(4, "validation.code"),
});
type OtpFormSchema = z.infer<typeof otpSchema>;

// Правила те же, что и при смене имени в профиле.
const nameSchema = z.object({
  name: nameValueSchema,
});
type NameFormSchema = z.infer<typeof nameSchema>;

/**
 * Шаги шторки: телефон → код → имя.
 *
 * Введённый код всегда уходит в `verify-otp`, и его ответ решает, что
 * дальше. `is_registered: true` — аккаунт уже есть: там же приходит токен и
 * профиль, это вход, имя спрашивать не у кого. `false` — номера в базе нет,
 * и последний шаг заводит его через `register` вместе с именем.
 *
 * `register` проверит код ещё раз, и на шаге имени он может истечь — тогда
 * начинаем цикл заново с телефона (см. restartRegistration), а не бросаем
 * пользователя с ошибкой.
 */
type Step = "phone" | "otp" | "name";

// Юридические документы под кнопкой входа. Открываются в браузере Telegram
// (openLink), мини-апп при этом остаётся открытым.
const LEGAL_LINKS = {
  privacy: "https://evos.uz/privacy_policy.pdf",
  loyalty: "https://evos.uz/loyalty_program_terms_and_conditions.pdf",
  personal: "https://evos.uz/ru/politics#offert-section-5",
} as const;

const LegalLink = ({
  href,
  children,
}: {
  href: string;
  children?: React.ReactNode;
}) => (
  <a
    href={href}
    onClick={(event) => {
      event.preventDefault();
      openExternalLink(href);
    }}
    className="text-text-primary underline underline-offset-2"
  >
    {children}
  </a>
);

type IProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
};

export const AuthDrawer = ({ open, setOpen, onSuccess }: IProps) => {
  const [step, setStep] = useState<Step>("phone");
  const dispatch = useDispatch();
  const user = useSelector(userInfo);
  const { sendOtp, isPending: isOTPSending } = useSendOtp();
  const { registerClient, isPending: isRegistering } = useRegisterClient();
  const { verifyOtp, isPending: isVerifying } = useVerifyOtp();
  // Текст отказа показывает попап (его шлют сервисы, см. custonSooner), а
  // здесь держим только признак: по нему поле кода подсвечивается красным.
  const [otpErrorMessage, setOtpErrorMessage] = useState("");
  // Номер, которому бэкенд отправить отказал (слишком рано, лимит на час):
  // кода от нас у пользователя нет, и «Продолжить» заперта до конца отсчёта.
  // Оба списка — по номеру: лимит у бэкенда считается именно так.
  const [throttledPhone, setThrottledPhone] = useState("");
  const resend = useCountdown();
  // Исчерпан лимит кодов на номер (десять в час): кнопки отправки заперты до
  // конца отсчёта, а не только подпись «повторно через».
  const [limitReached, setLimitReached] = useState(false);
  const isLimited = limitReached && !resend.isOver;
  // Пять неверных кодов подряд: проверка заперта на `retry_after`, пока
  // не запросят новый код. Отдельный отсчёт — у него свой срок.
  const verifyLock = useCountdown();
  const isVerifyLocked = !verifyLock.isOver;
  const form = useForm<PhoneFormSchema>({
    resolver: zodResolver(phoneSchema),
  });

  const otpForm = useForm<OtpFormSchema>({
    resolver: zodResolver(otpSchema),
  });

  const nameForm = useForm<NameFormSchema>({
    resolver: zodResolver(nameSchema),
    defaultValues: { name: "" },
  });
  const name = nameForm.watch("name") ?? "";

  /**
   * Запрос кода. Возвращает, разрешил ли бэкенд отправку, — только тогда
   * имеет смысл открывать шаг кода.
   */
  const requestOtp = async (phone: string) => {
    setOtpErrorMessage("");

    try {
      const data = await sendOtp(phone);
      setLimitReached(false);
      verifyLock.reset();
      resend.start(data.resend_after);
      return true;
    } catch (error) {
      const otpError = toOtpAuthError(error);

      // Код этому номеру уже уходил, и новый бэкенд пока не даст. На шаг
      // кода не пускаем: старый код у пользователя мог не сохраниться, а
      // запросить новый нельзя — ждём `retry_after` с кнопкой наготове.
      if (otpError.code === "resend_too_soon") {
        setThrottledPhone(phone);
        resend.start(otpError.retryAfter ?? DEFAULT_RESEND_AFTER);
        return false;
      }

      // Лимит на час: без `retry_after` ждём весь час — раньше бэкенд всё
      // равно откажет, а каждая попытка только продлевает блокировку.
      if (otpError.code === "too_many_requests") {
        setLimitReached(true);
        setThrottledPhone(phone);
        resend.start(otpError.retryAfter ?? LIMIT_RETRY_AFTER);
        return false;
      }

      // Остальные отказы (неверный формат, отказ шлюза) текстом уже показал
      // useSendOtp.
      return false;
    }
  };

  const onSubmit = async (data: PhoneFormSchema) => {
    // Запрос уходит всегда, но на шаг кода пускает только разрешение
    // бэкенда: `resend_too_soon` значит, что нового кода не будет, и делать
    // на этом шаге нечего — ждём `retry_after` с запертой кнопкой.
    if (await requestOtp(data.phone)) setStep("otp");
  };

  const onResend = async () => {
    if (!resend.isOver || isOTPSending) return;

    otpForm.reset();
    await requestOtp(form.getValues("phone"));
  };

  const closeDrawer = () => {
    setOpen(false);
    setStep("phone");
    form.reset();
    otpForm.reset();
    nameForm.reset();
    resend.reset();
    verifyLock.reset();
    setLimitReached(false);
    setThrottledPhone("");
    setOtpErrorMessage("");
  };

  /**
   * Код истёк — начинаем регистрацию заново: телефон, имя, новый код.
   *
   * Оставлять пользователя на шаге кода с истёкшим кодом бессмысленно:
   * вводить там уже нечего, а «отправить повторно» — это тот же путь, только
   * неочевидный. Телефон и имя оставляем введёнными, чтобы не набирать их
   * заново; возврат на первый шаг сам отправит новый код (две минуты к
   * этому моменту прошли, лимит бэкенда не мешает).
   */
  const restartRegistration = (message: string) => {
    otpForm.reset();
    setOtpErrorMessage(message);
    setStep("phone");
  };

  const finishAuth = () => {
    setOpen(false);
    setStep("phone");
    otpForm.reset();
    nameForm.reset();
    resend.reset();
    onSuccess?.();
  };

  /**
   * Новый номер: `register` — единственный запрос с кодом, он же его и
   * проверяет. Аккаунт появляется здесь, вместе с именем и токеном.
   */
  const registerWithOtp = async (phone: string, otp: string) => {
    const trimmed = nameForm.getValues("name").trim();
    const res = await registerClient({ phone, name: trimmed, otp });

    // Профиль перезапросится по инвалидации, но имя нужно на экране сразу —
    // иначе на главной мелькнёт заглушка «EVOS».
    dispatch(
      setUser({
        ...user,
        _id: String(res.client.id),
        name: res.client.name ?? trimmed,
        isAuth: true,
      }),
    );
    finishAuth();
  };

  /** Отказ по коду — с любого шага, что от `verify-otp`, что от `register`. */
  const handleOtpFailure = (otpError: ReturnType<typeof toOtpAuthError>) => {
    // Код просрочен — заново с телефона (см. restartRegistration).
    if (otpError.code === "code_expired") {
      restartRegistration(otpError.message);
      return;
    }

    // Опечатались в коде: возвращаем на шаг кода с чистым полем — имя
    // остаётся введённым, набирать заново не придётся.
    if (otpError.code === "invalid_code") {
      otpForm.reset();
      setOtpErrorMessage(otpError.message);
      setStep("otp");
      return;
    }

    // Попытки кончились: гасим шаг кода до `retry_after` и подгоняем под
    // него же отсчёт повтора — бэкенд просит именно новый код.
    if (otpError.code === "too_many_attempts") {
      otpForm.reset();
      setOtpErrorMessage(otpError.message);
      verifyLock.start(otpError.retryAfter);
      resend.start(otpError.retryAfter);
      setStep("otp");
    }
  };

  const onSubmitOtp = async (data: OtpFormSchema) => {
    setOtpErrorMessage("");

    try {
      const res = await verifyOtp({
        phone: form.getValues("phone"),
        otp: data.otp,
      });

      // Номер уже зарегистрирован: в ответе токен и профиль с именем — это
      // вход, шаг имени пропускаем.
      if (res.is_registered) {
        finishAuth();
        return;
      }

      // Номера в базе нет: аккаунт заведёт `register` вместе с именем.
      setStep("name");
    } catch (error) {
      handleOtpFailure(toOtpAuthError(error));
    }
  };

  /** Последний шаг нового номера: имя. Здесь же проверяется код. */
  const onSubmitName = async () => {
    try {
      await registerWithOtp(form.getValues("phone"), otpForm.getValues("otp"));
    } catch (error) {
      handleOtpFailure(toOtpAuthError(error));
    }
  };

  // Шаг имени обязательный: код уже проверен, но аккаунта без имени не
  // существует — `register` его ещё не создал. Закрыть шторку свайпом, тапом
  // по фону или кнопкой «назад» здесь нельзя, иначе человек остаётся с
  // подтверждённым номером и без аккаунта.
  const isNameRequired = step === "name";
  // Бэкенд отказал слать код на этот номер — кнопку не даём нажать, чтобы
  // запрос не уходил заведомо в отказ. Номера, которому код уже ушёл, это не
  // касается: по нему кнопка просто открывает шаг ввода.
  const isPhoneThrottled =
    !resend.isOver && form.watch("phone") === throttledPhone;

  return (
    <Drawer
      shouldScaleBackground={false}
      open={open}
      dismissible={!isNameRequired}
      onClose={() => {
        if (isNameRequired) return;
        setOpen(false);
      }}
      onOpenChange={(next: boolean) => {
        if (!next && isNameRequired) return;
        closeDrawer();
      }}
      scrollLockTimeout={0}
    >
      {step === "phone" && (
        <DrawerContent className="bg-fill-background  p-4 flex flex-col gap-y-2 items-center">
          <>
            <div />
            <div className="flex flex-col gap-y-4">
              <div className="flex flex-col space-y-2 items-center">
                <p className="text-display-lg">{t("auth.title")}</p>
                <p className="text-body-sm-regular text-text-secondary max-w-[188px] text-center">
                  {t("auth.description")}
                </p>
                <Form {...form}>
                  <form
                    className={cn(
                      "flex flex-col space-y-5 pb-8 pt-4 w-full h-full",
                      {},
                    )}
                  >
                    <PhoneField
                      name="phone"
                      inputMode="numeric"
                      className="w-full h-16 border-none text-[22px] font-semibold rounded-[16px] max-w-[343px] leading-7 tracking-[0px] caret-button-primary px-4 py-[18px]"
                    />
                    <LoadingButton
                      // Отдельного согласия не нужно: нажимая «Продолжить»,
                      // человек принимает условия — об этом текст под кнопкой.
                      disabled={isLimited || isPhoneThrottled}
                      loading={isOTPSending}
                      onClick={form.handleSubmit(onSubmit)}
                      className="w-full bg-button-primary h-12 text-[16px] font-semibold font-inter leading-5 tracking-[0px] text-text-white rounded-xl disabled:opacity-60"
                    >
                      {t("continueButton")}
                    </LoadingButton>
                    {/* w-0 min-w-full: текст не должен расширять форму — ширину задаёт поле
                        телефона, абзац переносится под неё. */}
                    <p className="w-0 min-w-full text-caption-sm-medium text-text-tertiary text-center">
                      <Trans
                        i18nKey="auth.legal"
                        components={{
                          privacy: <LegalLink href={LEGAL_LINKS.privacy} />,
                          loyalty: <LegalLink href={LEGAL_LINKS.loyalty} />,
                          personal: <LegalLink href={LEGAL_LINKS.personal} />,
                        }}
                      />
                    </p>
                  </form>
                </Form>
              </div>
            </div>
          </>
        </DrawerContent>
      )}
      {step === "name" && (
        <DrawerContent
          liftAboveKeyboard
          // Без «палочки»: свайпом шторка не закрывается, а её вид это
          // обещает. Выход только через «Назад» — на шаг кода, внутри той же
          // шторки.
          handleClassName="hidden"
          className="bg-fill-background px-4 pt-6 flex flex-col gap-y-2"
        >
          <div className="flex flex-row justify-between items-center">
            <button
              type="button"
              className="flex flex-row items-center space-x-[9px]"
              // На телефон, а не на код: введённый код уже проверен и
              // одноразовый, возвращаться к нему незачем — нужен новый, а его
              // запрашивает как раз первый шаг.
              onClick={() => {
                otpForm.reset();
                setOtpErrorMessage("");
                setStep("phone");
              }}
            >
              <ArrowRightIcon className="rotate-180 h-4 w-2" />
              <span className="text-display-md">{t("back")}</span>
            </button>
          </div>
          <div className="flex flex-col gap-y-4 pt-5 items-center">
            <div className="flex flex-col space-y-2 items-center">
              <DrawerTitle className="text-display-lg">
                {t("auth.name.title")}
              </DrawerTitle>
              <p className="text-body-sm-regular text-text-secondary max-w-[240px] text-center">
                {t("auth.name.description")}
              </p>
            </div>
            <Form {...nameForm}>
              <form
                className="flex flex-col space-y-5 pb-8 w-full"
                onSubmit={nameForm.handleSubmit(onSubmitName)}
              >
                <InputField
                  name="name"
                  maxLength={NAME_MAX_LENGTH}
                  autoComplete="name"
                  // onChange перекрывает обработчик react-hook-form (InputField
                  // раскладывает inputProps после field), поэтому значение
                  // кладём в форму сами — уже очищенным.
                  onChange={(e) =>
                    nameForm.setValue("name", sanitizeName(e.target.value), {
                      shouldDirty: true,
                    })
                  }
                  className="w-full h-16 border-none text-[22px] font-semibold rounded-[16px] leading-7 tracking-[0px] caret-button-primary px-4 py-[18px]"
                />
                <LoadingButton
                  disabled={!name.trim()}
                  // Шаг имени заводит аккаунт через `register`.
                  loading={isRegistering}
                  type="submit"
                  className="w-full bg-button-primary h-12 text-[16px] font-semibold font-inter leading-5 tracking-[0px] text-text-white rounded-xl disabled:opacity-60"
                >
                  {t("continueButton")}
                </LoadingButton>
              </form>
            </Form>
          </div>
        </DrawerContent>
      )}
      {step === "otp" && (
        <DrawerContent className="bg-fill-background  px-4  flex flex-col gap-y-2">
          <div className="flex flex-row justify-between items-center">
            <div
              className="flex flex-row items-center space-x-[9px]"
              onClick={() => {
                otpForm.reset();
                setOtpErrorMessage("");
                setStep("phone");
              }}
            >
              <ArrowRightIcon className="rotate-180 h-4 w-2  " />
              <DrawerTitle className="text-display-md">{t("back")}</DrawerTitle>
            </div>
          </div>
          <div className="flex flex-col gap-y-4 pt-5 items-center">
            <div className="flex flex-col space-y-2 items-center">
              <p className="text-display-lg">{t("auth.otp.title")}</p>
              <div>
                <p className="text-body-sm-regular text-text-secondary  text-center">
                  {t("auth.otp.description")}
                </p>
                <p className="text-body-sm-regular text-text-secondary  text-center">
                  {formatPhoneNumberWithCode(form.getValues("phone"))}
                </p>
              </div>
            </div>
            <Form {...otpForm}>
              <form className="flex flex-col space-y-8 w-full pb-6">
                <div className="flex flex-col space-y-4">
                  <Controller
                    control={otpForm.control}
                    name="otp"
                    render={({
                      field,
                      fieldState: { error },
                      formState: { isSubmitted },
                    }) => (
                      <CustomOTP
                        value={field.value || ""}
                        onChange={(val) => {
                          field.onChange(val);
                          if (error) otpForm.clearErrors("otp");
                          if (otpErrorMessage) setOtpErrorMessage("");
                        }}
                        length={4}
                        error={(isSubmitted && !!error) || !!otpErrorMessage}
                      />
                    )}
                  />
                  {/* Повторный код: пока идёт отсчёт — подпись с таймером,
                      после — кнопка. */}
                  {isVerifyLocked ? (
                    <p className="text-body-sm-regular text-fill-danger text-center">
                      {t("auth.otp.tooManyAttempts", {
                        time: formatCountdown(verifyLock.secondsLeft),
                      })}
                    </p>
                  ) : resend.isOver ? (
                    <button
                      type="button"
                      onClick={onResend}
                      disabled={isOTPSending}
                      className="text-body-sm-medium text-brand-green text-center disabled:opacity-60"
                    >
                      {t("auth.otp.resend")}
                    </button>
                  ) : (
                    <p
                      className={cn("text-body-sm-regular text-center", {
                        "text-fill-danger": isLimited,
                        "text-text-tertiary": !isLimited,
                      })}
                    >
                      {t(
                        isLimited
                          ? "auth.otp.limitReached"
                          : "auth.otp.resendIn",
                        {
                          time: formatCountdown(resend.secondsLeft),
                        },
                      )}
                    </p>
                  )}
                </div>
                <LoadingButton
                  disabled={isVerifyLocked}
                  // Код проверяет `verify-otp` — пока ждём его, крутим лоадер.
                  loading={isVerifying}
                  onClick={otpForm.handleSubmit(onSubmitOtp)}
                  className="w-full bg-button-primary h-12 text-[16px] font-semibold font-inter leading-5 tracking-[0px] text-text-white rounded-xl disabled:opacity-60"
                >
                  {t("continueButton")}
                </LoadingButton>
              </form>
            </Form>
          </div>
        </DrawerContent>
      )}
    </Drawer>
  );
};
